import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { CALL_PRICING_MODEL_FLAT_RATE } from '../../../../shared/constants/common';
import { updateCreator } from '../../../../shared/actions/creatorActions';
import { fetchUser } from '../../../../shared/actions/userActions';
import { showProfileUpdatedPushToast } from '../../../../shared/pusNotification/useManualToasts';
import { showError } from '../../../../shared/utils/swalHandler';
import { getCallRequestPricingDetails } from './utils/callFormatUtils';
import {
  buildCallSettingsProfileUpdatePayload,
  clampFlatRateDurationMinutes,
} from './utils/callRateOfferUtils';
import { isVideoCallKind, navigateToCallRoute, normalizeCallKind } from './constant';
import { useRemoteSubscriberWalletCredits } from './hooks/useRemoteSubscriberWalletCredits';
import { formatAmountWithTwoDecimalPlaces } from '../../../../shared/utils/function';

const FLAT_RATE_MIN_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MIN_DURATION_MINUTES;
const FLAT_RATE_MAX_DURATION = CALL_PRICING_MODEL_FLAT_RATE.MAX_DURATION_MINUTES;
const CALL_RATE_MAX_AMOUNT = 1000;
const AMOUNT_INPUT_PATTERN = /^\d*(?:\.\d{0,2})?$/;
const INTEGER_INPUT_PATTERN = /^\d*$/;

const HELP_TEXT = 'You keep 100% of what you charge and subs pay the platform fee';

const toInputValue = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '';
};

const parseAmountInput = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || trimmed === '.') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 && n <= CALL_RATE_MAX_AMOUNT ? n : null;
};

const parseDurationInput = (value) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

const getAmountError = (value, label) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || trimmed === '.') return `${label} is required.`;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return `${label} must be greater than $0.`;
  if (n > CALL_RATE_MAX_AMOUNT) return `${label} cannot exceed $${CALL_RATE_MAX_AMOUNT}.`;
  return '';
};

const getDurationError = (value, label, { min = 1, max } = {}) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return `${label} is required.`;
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return `${label} must be a whole number.`;
  if (n < min) return `${label} must be at least ${min} minute${min === 1 ? '' : 's'}.`;
  if (max && n > max) return `${label} cannot exceed ${max} minutes.`;
  return '';
};

const normalizeAmountInputOnBlur = (value, setValue) => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || trimmed === '.') {
    setValue('');
    return;
  }
  setValue(trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed);
};

const CreatorCallRateModal = ({
  isOpen,
  onClose,
  callKind = 'voice',
  roomId,
  otherUserId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const currentUser = useSelector((state) => state.user?.user);
  const kind = normalizeCallKind(callKind);
  const isVideo = isVideoCallKind(kind);
  const settings = currentUser?.[kind] || {};
  const initialPricingModel = settings?.pricingModel === 'flat_rate' ? 'flat_rate' : 'per_minute';
  const initialPerMinuteAmount = Number(settings?.perMinute?.amount) || 10;
  const initialMinDurationMinutes = Number(settings?.perMinute?.minCallMinutess) || 1;
  const hasInitialMinDuration = initialMinDurationMinutes > 1;
  const initialFlatRateAmount = Number(settings?.flatRate?.amount) || 50;
  const initialFlatRateDurationMinutes =
    Number(settings?.flatRate?.durationMinutes) || CALL_PRICING_MODEL_FLAT_RATE.DURATION;
  const callOpts = roomId ? { roomId } : {};
  const [pricingModel, setPricingModel] = useState(initialPricingModel);
  const [perMinuteAmount, setPerMinuteAmount] = useState(toInputValue(initialPerMinuteAmount));
  const [flatRateAmount, setFlatRateAmount] = useState(toInputValue(initialFlatRateAmount));
  const [flatRateDurationMinutes, setFlatRateDurationMinutes] = useState(toInputValue(initialFlatRateDurationMinutes));
  const [hasMinDuration, setHasMinDuration] = useState(hasInitialMinDuration);
  const [minDurationMinutes, setMinDurationMinutes] = useState(toInputValue(hasInitialMinDuration ? initialMinDurationMinutes : 1));
  const [isSavingRate, setIsSavingRate] = useState(false);
  const {
    credits: remoteSubscriberCredits,
    loading: remoteSubscriberCreditsLoading,
  } = useRemoteSubscriberWalletCredits(otherUserId, { enabled: isOpen });

  useEffect(() => {
    if (!isOpen) return;
    setPricingModel(initialPricingModel);
    setPerMinuteAmount(toInputValue(initialPerMinuteAmount));
    setFlatRateAmount(toInputValue(initialFlatRateAmount));
    setFlatRateDurationMinutes(toInputValue(initialFlatRateDurationMinutes));
    setHasMinDuration(hasInitialMinDuration);
    setMinDurationMinutes(toInputValue(hasInitialMinDuration ? initialMinDurationMinutes : 1));
  }, [
    isOpen,
    initialPricingModel,
    initialPerMinuteAmount,
    initialFlatRateAmount,
    initialFlatRateDurationMinutes,
    hasInitialMinDuration,
    initialMinDurationMinutes,
  ]);

  // Hydrate from Redux when the modal opens, and again if profile data arrives while
  // the modal is open — unless the creator already picked a pricing model this session,
  // so we do not reset Flat Rate back to Per Minute on re-fetch.
  const wasModalOpenRef = useRef(false);
  const userChosePricingModelRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasModalOpenRef.current = false;
      userChosePricingModelRef.current = false;
      return;
    }
    const isFirstOpenThisSession = !wasModalOpenRef.current;
    if (isFirstOpenThisSession) wasModalOpenRef.current = true;
    if (!isFirstOpenThisSession && userChosePricingModelRef.current) return;

    setPricingModel(initialPricingModel);
    setPerMinuteAmount(toInputValue(initialPerMinuteAmount));
    setFlatRateAmount(toInputValue(initialFlatRateAmount));
    setFlatRateDurationMinutes(toInputValue(initialFlatRateDurationMinutes));
    setHasMinDuration(hasInitialMinDuration);
    setMinDurationMinutes(toInputValue(hasInitialMinDuration ? initialMinDurationMinutes : 1));
  }, [
    isOpen,
    initialPricingModel,
    initialPerMinuteAmount,
    initialFlatRateAmount,
    initialFlatRateDurationMinutes,
    initialMinDurationMinutes,
    hasInitialMinDuration,
  ]);

  const perMinuteAmountValue = parseAmountInput(perMinuteAmount);
  const flatRateAmountValue = parseAmountInput(flatRateAmount);
  const flatRateDurationValue = parseDurationInput(flatRateDurationMinutes);
  const minDurationValue = parseDurationInput(minDurationMinutes);

  const currentRateText = useMemo(() => {
    if (pricingModel === 'flat_rate') {
      const amount = flatRateAmountValue ?? initialFlatRateAmount;
      const duration = flatRateDurationValue ?? initialFlatRateDurationMinutes;
      return `$${formatAmountWithTwoDecimalPlaces(amount)} per ${duration} min`;
    }
    const amount = perMinuteAmountValue ?? initialPerMinuteAmount;
    return `$${formatAmountWithTwoDecimalPlaces(amount)}/minute`;
  }, [
    pricingModel,
    flatRateAmountValue,
    flatRateDurationValue,
    perMinuteAmountValue,
    initialFlatRateAmount,
    initialFlatRateDurationMinutes,
    initialPerMinuteAmount,
  ]);
  const perMinuteValidationMessage =
    getAmountError(perMinuteAmount, 'Rate per minute') ||
    (hasMinDuration ? getDurationError(minDurationMinutes, 'Minimum duration') : '');
  const flatRateValidationMessage =
    getAmountError(flatRateAmount, 'Flat rate price') ||
    getDurationError(flatRateDurationMinutes, 'Flat rate duration', {
      min: FLAT_RATE_MIN_DURATION,
      max: FLAT_RATE_MAX_DURATION,
    });
  const validationMessage =
    pricingModel === 'flat_rate' ? flatRateValidationMessage : perMinuteValidationMessage;

  const previewCreatorInfo = useMemo(() => {
    const nextUser = { ...(currentUser || {}) };
    nextUser[kind] = {
      pricingModel,
      perMinute: {
        amount: perMinuteAmountValue ?? 0,
        minCallMinutess: hasMinDuration ? minDurationValue ?? 0 : 0,
      },
      flatRate: {
        amount: flatRateAmountValue ?? 0,
        durationMinutes: flatRateDurationValue ?? 0,
      },
    };
    return nextUser;
  }, [
    currentUser,
    kind,
    pricingModel,
    perMinuteAmountValue,
    hasMinDuration,
    minDurationValue,
    flatRateAmountValue,
    flatRateDurationValue,
  ]);

  const pricingResult = useMemo(
    () => getCallRequestPricingDetails(previewCreatorInfo, kind),
    [previewCreatorInfo, kind]
  );
  const minimumCreditsRequired = pricingResult?.ok ? pricingResult.minCostCredits : null;
  const canSendRequest =
    !validationMessage &&
    Boolean(pricingResult?.ok) &&
    !remoteSubscriberCreditsLoading &&
    Number.isFinite(remoteSubscriberCredits) &&
    remoteSubscriberCredits >= minimumCreditsRequired;
  const selectPricingModel = (nextModel) => {
    userChosePricingModelRef.current = true;
    setPricingModel(nextModel);
  };

  const handleSendRequest = () => {
    // if (!canSendRequest || validationMessage) return;
    if (validationMessage) return;
    
    onClose?.();
    const callRateOpts = {
      ...callOpts,
      pricingModel,
      perMinuteAmount: perMinuteAmountValue,
      minDurationMinutes: hasMinDuration ? minDurationValue : 0,
      flatRateAmount: flatRateAmountValue,
      flatRateDurationMinutes:
        pricingModel === 'flat_rate'
          ? clampFlatRateDurationMinutes(flatRateDurationValue)
          : flatRateDurationValue,
    };
    void navigateToCallRoute(navigate, kind, callRateOpts);
  };

  const handleSetRate = async () => {
    if (validationMessage || isSavingRate) return;

    const callSettings = buildCallSettingsProfileUpdatePayload(settings, {
      pricingModel,
      perMinuteAmount: perMinuteAmountValue,
      minCallMinutess: hasMinDuration ? minDurationValue : 0,
      flatRateAmount: flatRateAmountValue,
      flatRateDurationMinutes: flatRateDurationValue ?? initialFlatRateDurationMinutes,
    });

    setIsSavingRate(true);
    try {
      const result = await updateCreator({ [kind]: callSettings });
      if (!result?.success) {
        throw new Error(result?.error || 'Failed to save call rate. Please try again.');
      }

      await dispatch(fetchUser());
      showProfileUpdatedPushToast(currentUser);
      onClose?.();
    } catch (error) {
      await showError('Error', error?.message || 'Failed to save call rate. Please try again.');
    } finally {
      setIsSavingRate(false);
    }
  };

  useEffect(() => {
    if (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) {
      document.body.classList.add('chrome-only');
    }

    if (/iPhone/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent)) {
      document.body.classList.add('iphone-safari');
    }
  }, []);

  return (
    <Modal isOpen={isOpen} centered className="cm_call_info_modal creator_call_rate_modal">
      <div className="cm_call_info_header d-flex align-items-center justify-center mb-3">
        <div className="m_p_icon m_p_icon2">
          <i className={isVideo ? 'fa-solid fa-video' : 'fa-solid fa-phone'} aria-hidden />
        </div>
        <h5 className="m-0">Set {isVideo ? 'Video' : 'Call'} Rate</h5>
      </div>

      <div className="mb-3">
        <h6 className="mb-2">Choose your pricing model:</h6>
        <div className={`cm_my_price_modal cm_call_rate_tile ${pricingModel === 'per_minute' ? 'active' : ''}`} onClick={() => selectPricingModel('per_minute')}>
          <div className='cm_my_price_modal_radio'></div>
          <div className='cm_my_price_modal_title'>Per minute rate</div>
          <div className="cm_my_price_modal_sub_title">Charge by the minute</div>
        </div>

        <div className={`cm_my_price_modal cm_call_rate_tile ${pricingModel === 'flat_rate' ? 'active' : ''}`} onClick={() => selectPricingModel('flat_rate')}>
          <div className='cm_my_price_modal_radio'></div>
          <div className='cm_my_price_modal_title'>Flat Rate</div>
          <div className="cm_my_price_modal_sub_title">Fixed price for set duration</div>
        </div>
      </div>

      {pricingModel === 'flat_rate' ? (
        <div className="mb-3">
          <div className="cm_call_rate_dual_labels mb-2">
            <span className="fw-bold">Price ($)</span>
            <span className="fw-bold">Duration (min)</span>
          </div>
          <div className="cm_call_rate_dual_inputs">
            <div className="cm_call_rate_readonly_input cm_call_rate_tile">
              <span className="cm_call_rate_dollar">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={flatRateAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (AMOUNT_INPUT_PATTERN.test(value)) setFlatRateAmount(value);
                }}
                onBlur={() => normalizeAmountInputOnBlur(flatRateAmount, setFlatRateAmount)}
              />
            </div>
            <div className="cm_call_rate_readonly_input cm_call_rate_tile">
              <input
                type="text"
                inputMode="numeric"
                value={flatRateDurationMinutes}
                onChange={(e) => {
                  const value = e.target.value;
                  if (INTEGER_INPUT_PATTERN.test(value)) setFlatRateDurationMinutes(value);
                }}
              />
            </div>
          </div>
          {validationMessage && <small className="text-danger d-block mt-2">{validationMessage}</small>}
          <small className="text-muted d-block mt-2">{HELP_TEXT}</small>
        </div>
      ) : (
        <div className="mb-3">
          <span className="fw-bold">Rate per minute ($)</span>
          <div className="cm_call_rate_readonly_input cm_call_rate_tile mt-2">
            <span className="cm_call_rate_dollar">$</span>
            <input
              type="text"
              inputMode="decimal"
              value={perMinuteAmount}
              onChange={(e) => {
                const value = e.target.value;
                if (AMOUNT_INPUT_PATTERN.test(value)) setPerMinuteAmount(value);
              }}
              onBlur={() => normalizeAmountInputOnBlur(perMinuteAmount, setPerMinuteAmount)}
            />
          </div>
          {validationMessage && <small className="text-danger d-block mt-2">{validationMessage}</small>}
          <small className="text-muted d-block mt-2">{HELP_TEXT}</small>
        </div>
      )}

      {pricingModel === 'per_minute' && (
        <div className={`cm_call_info_card cm_call_info_card_2 mb-3 cm_call_min_duration_card ${hasMinDuration ? 'active' : ''}`}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold">Minimum call duration</div>
              <small className="text-muted">Set a minimum billing time</small>
            </div>
            <div
              className={`cm_call_rate_switch ${hasMinDuration ? 'active' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => setHasMinDuration((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setHasMinDuration((prev) => !prev);
              }}
            >
              <span className="cm_call_rate_switch_thumb" />
            </div>
          </div>
          {hasMinDuration && (
            <div className="mt-3">
              <span className="fw-bold">Minimum duration (minutes)</span>
              <div className="cm_call_rate_readonly_input cm_call_rate_tile mt-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={minDurationMinutes}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (INTEGER_INPUT_PATTERN.test(value)) setMinDurationMinutes(value);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-3 mb-3 cm_call_info_card cm_call_info_card_2 cm_call_current_rate_disabled">
        <small className="text-muted d-block">Current rate</small>
        <div className="fw-bold">{currentRateText}</div>
      </div>

      <div className="p-3 mm_warinig_box mb-3">
        <i className="fa-solid fa-circle-info" aria-hidden />
        If you set a minimum call time or flat rate and you end the call early, you will only be paid
        per minute based on your rate. If the subscriber ends the call early you will still receive
        your full set rate.
      </div>

      <div className="mb-3">
        <span
          // className={`jh_btn_pink w-100 text-center${!canSendRequest ? ' opacity-50' : ''}`}
          className={`jh_btn_pink w-100 text-center`}
          onClick={handleSendRequest}
          role="button"
          tabIndex={canSendRequest ? 0 : -1}
          aria-disabled={!canSendRequest}
        >
          {remoteSubscriberCreditsLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
              Checking subscriber credits...
            </>
          ) : (
            <>Send {isVideo ? 'Video' : 'Call'}  Request</>
          )}
        </span>
      </div>

      <div className="d-flex gap-3 cm_call_modal_action_row">
        <span
          className="jh_btn_gray text-no-wrap cm_call_modal_action_btn"
          onClick={onClose}
          role="button"
          tabIndex={0}
        >
          Cancel
        </span>
        <span
          className={`jh_btn_border_pink text-no-wrap cm_call_modal_action_btn${validationMessage || isSavingRate ? ' opacity-50' : ''}`}
          onClick={handleSetRate}
          role="button"
          tabIndex={validationMessage || isSavingRate ? -1 : 0}
          aria-disabled={Boolean(validationMessage) || isSavingRate}
        >
          {isSavingRate ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
              Saving...
            </>
          ) : (
            'Set Rate'
          )}
        </span>
      </div>
    </Modal>
  );
};

export default CreatorCallRateModal;
